// @ts-nocheck
/* eslint-disable */
import React, { useState } from 'react';
import AddEntity from '../../components/CRUD/AddEntity';
import { useTypedSelector } from '../../../helpers';
import RouteTransferList from './RouteTransferList';
import DiscountBandGroupStats from './DiscountBandGroupStats';
import PriorityReferenceTable from './PriorityReferenceTable';
import { ApplicableFaresPanel, passesFareGuard } from '../shared/applicableFares';

const RouteSelectionField = ({ formValues, setFormValues, dropdownOptions }: any) => {
  const bandOptions = (dropdownOptions?.discount_bands_id) || [];
  const selectedBand = bandOptions.find((opt: any) => opt.discount_bands_id === formValues.discount_bands_id);
  const orgId = selectedBand?.organisations_id;

  // Track organisation change to reset routes
  const prevOrgIdRef = React.useRef(orgId);

  React.useEffect(() => {
    if (prevOrgIdRef.current && orgId && prevOrgIdRef.current !== orgId) {
      // Organization changed! Clear selected routes
      setFormValues((prev: any) => ({ ...prev, routes_ids: [] }));
    }
    prevOrgIdRef.current = orgId;
  }, [orgId, setFormValues]);

  return (
    <div style={{ width: '100%', padding: '0px 0', gridColumn: '1 / -1' }}>
      <RouteTransferList
        organisationId={orgId}
        selectedIds={formValues.routes_ids || []}
        onChange={(ids: any) => setFormValues((prev: any) => ({ ...prev, routes_ids: ids }))}
      />
    </div>
  );
};

const AddDiscountBandAssignment = () => {
  const userAuthData = useTypedSelector(state => state.userAuthData);
  // beforeSubmit receives only formValues, but the band's discount_amount lives
  // on the dropdown options that renderAboveSubmit is handed. Stash it as it
  // renders so the submit-time guard can read it.
  const bandMetaRef = React.useRef({ discountAmount: null, orgId: null });
  const intlRef = React.useRef(null);

  const inputs = React.useMemo(() => [
    { type: 'divider', labelKey: 'BasicInfo' },
    {
      name: 'discount_bands_id',
      labelKey: 'DiscountBand',
      type: 'dropdown',
      required: true,
      apiUrl: `${process.env.REACT_APP_ABT_ALL_APIS_BASE_URL}/api/v1/web_portal/fare_media_policy_capping/discount_band`,
      apiMethod: 'GET',
      dataPath: 'data.data.discountBands',
      optionConfig: { valueKey: 'discount_bands_id', labelKey: 'name' },
      renderExtra: (selectedValue, options, intl) => {
        if (!selectedValue) return null;
        const selectedBand = options.find((opt) => opt.discount_bands_id === selectedValue);
        if (!selectedBand) return null;
        const isAr = localStorage.getItem('EASY_MOBILITY_LANGUAGE') === 'ar-eg';
        return (
          <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f0f5ff', border: '1px solid #d6e4ff', borderRadius: '4px', fontSize: '0.85rem', color: '#1a237e' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>{intl?.formatMessage({ id: 'BandDetails' }) || 'Band Details'}:</div>

            {/* Organisation — auto-derived from band, shown as read-only */}
            {(selectedBand.org_en_name || selectedBand.org_ar_name) && (
              <div>
                <span style={{ fontWeight: '600' }}>{intl?.formatMessage({ id: 'Organisation' }) || 'Organisation'}: </span>
                {isAr ? (selectedBand.org_ar_name || selectedBand.org_en_name) : (selectedBand.org_en_name || selectedBand.org_ar_name)}
              </div>
            )}

            <div>{intl?.formatMessage({ id: 'Type' }) || 'Type'}: {selectedBand.discount_pct !== null ? (intl?.formatMessage({ id: 'Percentage' }) || 'Percentage') : (intl?.formatMessage({ id: 'FixedAmount' }) || 'Fixed Amount')}</div>
            <div>{intl?.formatMessage({ id: 'Value' }) || 'Value'}: {selectedBand.discount_pct !== null ? `${selectedBand.discount_pct}%` : `${selectedBand.discount_amount} EGP`}</div>
            <div>{intl?.formatMessage({ id: 'DateWindow' }) || 'Date Window'}: {selectedBand.valid_date_from ? selectedBand.valid_date_from.split('T')[0] : (intl?.formatMessage({ id: 'Open' }) || 'Open')} - {selectedBand.valid_date_to ? selectedBand.valid_date_to.split('T')[0] : (intl?.formatMessage({ id: 'Open' }) || 'Open')}</div>
            {(selectedBand.valid_time_from || selectedBand.valid_time_to) && (
              <div>{intl?.formatMessage({ id: 'TimeWindow' }) || 'Time Window'}: {selectedBand.valid_time_from || (intl?.formatMessage({ id: 'Open' }) || 'Open')} - {selectedBand.valid_time_to || (intl?.formatMessage({ id: 'Open' }) || 'Open')}</div>
            )}
          </div>
        );
      }
    },
    // NOTE: organisations_id dropdown is intentionally removed.
    // The org is automatically taken from the selected discount band to prevent mismatches.

    {
      name: 'passengers_groups_id',
      labelKey: 'PassengersGroup',
      type: 'dropdown',
      required: true,
      dependsOn: 'discount_bands_id',
      apiUrl: (formValues, dropdownOptions) => {
        const bandOptions = (dropdownOptions?.discount_bands_id) || [];
        const selectedBand = bandOptions.find(opt => opt.discount_bands_id === formValues.discount_bands_id);
        const orgId = selectedBand?.organisations_id;
        return `${process.env.REACT_APP_ABT_ALL_APIS_BASE_URL}/api/v1/web_portal/fare_media_policy_capping/passengers_groups?page=1&page_count=100&export=0&lang=${localStorage.getItem('EASY_MOBILITY_LANGUAGE') === 'ar-eg' ? 'ar' : 'en'}${orgId ? `&organisation_id=${orgId}` : ''}`;
      },
      apiMethod: 'GET',
      dataPath: 'data.data.passengersGroups',
      optionConfig: { valueKey: 'passengers_groups_id', labelKey: 'name' },
      renderExtra: (selectedValue: any, options: any, intl: any, setFormValues: any, formValues: any) => {
        return <DiscountBandGroupStats formValues={formValues} intl={intl} />;
      }
    },
    { type: 'divider', labelKey: 'Validity' },
    { name: 'valid_from', labelKey: 'AssignmentValidFrom', type: 'date', required: true, defaultValue: new Date().toISOString().split('T')[0] },
    { name: 'valid_to', labelKey: 'AssignmentValidTo', type: 'date' },
    { 
      name: 'priority', 
      labelKey: 'Priority', 
      type: 'number', 
      required: true, 
      defaultValue: 100,
      hint: 'PriorityHint'
    },
    {
      name: 'priority_reference_table',
      labelKey: 'PriorityReference', // Doesn't need a label visually since it's custom, but helps internal mapping
      type: 'custom',
      render: (formValues: any, setFormValues: any, intl: any) => (
        <div style={{ gridColumn: '1 / -1', marginTop: '-10px', marginBottom: '10px' }}>
          <PriorityReferenceTable formValues={formValues} intl={intl} />
        </div>
      )
    },
    {
      name: 'apply_all_routes',
      labelKey: 'ApplyOnAllRoutes',
      type: 'checkbox',
      defaultValue: true,
      visibleIf: (values) => !!values.discount_bands_id,
      sx: { mb: 1, mt: 1 }
    },
    {
      name: 'routes_ids',
      labelKey: 'Routes',
      type: 'custom',
      dependsOn: 'apply_all_routes',
      defaultValue: [],
      visibleIf: (values) => !!values.discount_bands_id && !values.apply_all_routes,
      render: (formValues, setFormValues, intl, dropdownOptions) => (
        <RouteSelectionField 
          formValues={formValues} 
          setFormValues={setFormValues} 
          dropdownOptions={dropdownOptions} 
        />
      )
    },
  ], []);

  return (
    <AddEntity
      pageTitle="AddNewDiscountBandAssignment"
      subsystemName="DiscountBandAssignment"
      permissionSystem="fares"
      backUrl="/fare-products/discount-band-assignment"
      submitApiUrl={`${process.env.REACT_APP_ABT_ALL_APIS_BASE_URL}/api/v1/web_portal/fare_media_policy_capping/discount_band_assignment`}
      itemsPerColumn={6}
      inputs={inputs}
      dropdowns={[]}

      buildSubmitPayload={(values, dropdownOptions) => {
        const payload = { ...values };

        // Auto-inject organisations_id from the selected discount band
        const bandOptions = (dropdownOptions?.discount_bands_id) || [];
        const selectedBand = bandOptions.find(opt => opt.discount_bands_id === values.discount_bands_id);
        if (selectedBand?.organisations_id) {
          payload.organisations_id = selectedBand.organisations_id;
        }

        if (payload.valid_from) payload.valid_from = payload.valid_from.split('T')[0];
        if (payload.valid_to)   payload.valid_to   = payload.valid_to ? payload.valid_to.split('T')[0] : null;
        else payload.valid_to = null;
        
        if (payload.apply_all_routes) {
          payload.routes_ids = [];
        } else if (!payload.routes_ids) {
          payload.routes_ids = [];
        }
        delete payload.apply_all_routes; // Remove virtual field before sending to API
        return payload;
      }}

      successMessage="DiscountBandAssignmentCreatedSuccessfully"

      beforeSubmit={async (values) => {
        // Routes are known here, so this is the point where a fixed discount
        // larger than the cheapest chargeable fare (base fare OR zone price)
        // must be stopped. Left unchecked it does not error at tap time — it
        // silently makes those trips free.
        const ok = await passesFareGuard({
          scope: 'assignment',
          discountAmount: bandMetaRef.current.discountAmount,
          intl: intlRef.current,
          body: {
            discount_bands_id: values.discount_bands_id,
            valid_from: values.valid_from,
            routes_ids: values.apply_all_routes ? [] : (values.routes_ids || []),
          },
        });
        return { proceed: ok };
      }}

      renderAboveSubmit={(formValues, setFormValues, intl, dropdownOptions) => {
        // Org and discount value are derived from the selected band — the
        // assignment form has no organisation field of its own.
        const bandOptions = (dropdownOptions?.discount_bands_id) || [];
        const selectedBand = bandOptions.find(opt => opt.discount_bands_id === formValues.discount_bands_id);

        bandMetaRef.current = {
          discountAmount: selectedBand?.discount_amount ?? null,
          orgId: selectedBand?.organisations_id ?? null,
        };
        intlRef.current = intl;

        return (
          <ApplicableFaresPanel
            scope="assignment"
            intl={intl}
            discountAmount={selectedBand?.discount_amount}
            missingKey="PleaseSelectBandAndDate"
            hintKey="AssignmentFaresHint"
            buildBody={() => {
              if (!formValues.discount_bands_id || !formValues.valid_from) return null;
              return {
                discount_bands_id: formValues.discount_bands_id,
                valid_from: formValues.valid_from,
                routes_ids: formValues.apply_all_routes ? [] : (formValues.routes_ids || []),
              };
            }}
          />
        );
      }}
    />
  );
};

export default AddDiscountBandAssignment;
